export interface ProtocolField {
  id: number;
  name: string;
  type: 'free-text' | 'number' | 'select' | 'checkbox' | 'date';
  description?: string;
  required: boolean;
  default_value?: string;
  choices?: string[];
  range_min?: number;
  range_max?: number;
  label: string;
  protocol_id: number;
}

export interface ProtocolBlock {
  id: string;
  title: string;
  fields: ProtocolField[];
  order: number;
}

export interface ProtocolSection {
  name: string;
  description?: string;
  fields?: Array<{
    label: string;
    description?: string;
    value: string | number | boolean | object;
  }>;
  value?: string | number | boolean | object;
}

export interface ProtocolMessage {
  protocol: {
    name?: string;
    description?: string;
    sections: ProtocolSection[];
  };
}

export interface UserProtocol {
  id: number;
  name: string;
  description?: string;
  content: string; // JSON строка с структурой протокола
  specialty?: string;
  is_active: boolean;
  user_id: number;
  created_at: string;
  updated_at: string;
}

// Type guard function to validate protocol message structure
export function isValidProtocolMessage(obj: any): obj is ProtocolMessage {
  if (!obj || typeof obj !== 'object' || !obj.protocol || typeof obj.protocol !== 'object' || !Array.isArray(obj.protocol.sections)) {
    return false;
  }

  return obj.protocol.sections.every((section: any) => {
    if (!section || typeof section !== 'object') {
      return false;
    }

    // Поддержка элементов с label вместо name (преобразуем их в секции)
    const sectionName = section.name || section.label;
    if (typeof sectionName !== 'string') {
      return false;
    }

    // Если элемент имеет label вместо name, но имеет value, это валидная секция с одним значением
    if (section.label && !section.name && section.value !== undefined) {
      return (
        typeof section.value === 'string' || 
        typeof section.value === 'number' || 
        typeof section.value === 'boolean' ||
        typeof section.value === 'object'
      );
    }

    // Стандартная валидация секции
    if (section.fields !== undefined) {
      if (!Array.isArray(section.fields)) {
        return false;
      }
      return section.fields.every((field: any) => 
        field &&
        typeof field === 'object' &&
        typeof field.label === 'string' &&
        (field.value === undefined || 
         typeof field.value === 'string' || 
         typeof field.value === 'number' || 
         typeof field.value === 'boolean' ||
         typeof field.value === 'object')
      );
    }

    // Проверка значения секции, если fields отсутствует
    if (section.value !== undefined) {
      return (
        typeof section.value === 'string' || 
        typeof section.value === 'number' || 
        typeof section.value === 'boolean' ||
        typeof section.value === 'object'
      );
    }

    // Секция без полей и без значения тоже валидна (пустая секция)
    return true;
  });
}

export interface CreateProtocolRequest {
  name: string;
  description?: string;
  content: string; // JSON строка с структурой протокола
  specialty?: string; // Опционально
}

export interface UpdateProtocolRequest {
  name?: string;
  description?: string;
  content?: string; // JSON строка с структурой протокола
  specialty?: string;
  is_active?: boolean;
}

export interface ProtocolFilters {
  specialty?: string;
  status?: 'all' | 'active' | 'inactive';
  search?: string;
  skip?: number;
  limit?: number;
}

export interface ProtocolListResponse {
  protocols: UserProtocol[];
  total: number;
  skip: number;
  limit: number;
  page: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

